import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { isProfileComplete } from '../../lib/rolePermissions';
import '../../styles/Auth.css';

/**
 * Handles OAuth redirect callbacks and PKCE/hash token resolution.
 * Supports both ?code=... (PKCE) and #access_token=... (hash flow).
 * Strips tokens from the address bar and routes deterministically.
 */
export default function AuthCallback() {
  const [error, setError] = useState('');
  const { createProfile, refreshUserData } = useAuth();
  const navigate = useNavigate();

  const hasNavigatedRef = useRef(false);
  const processingRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    let authSubscription = null;
    let fallbackTimer = null;

    const processUserSession = async (currentUser) => {
      if (!isMounted || hasNavigatedRef.current || processingRef.current) return;
      processingRef.current = true;

      try {
        console.log('[AuthCallback] Establishing user session for:', currentUser.email);
        const meta = currentUser.user_metadata || {};

        // 1. Fetch current profile
        const { data: existingProfile, error: profileQueryError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', currentUser.id)
          .maybeSingle();

        if (profileQueryError && profileQueryError.code !== 'PGRST116') {
          console.warn('[AuthCallback] Profile query notice:', profileQueryError.message);
        }

        const oauthIntent = (() => {
          try {
            return sessionStorage.getItem('google_oauth_intent');
          } catch {
            return null;
          }
        })();

        // Part 6: If user clicked "Continue with Google" on LOGIN page without having a registered artist account
        if (!existingProfile && oauthIntent === 'login') {
          console.warn('[AuthCallback] Google login rejected: No registered artist account found for:', currentUser.email);
          try {
            sessionStorage.removeItem('google_oauth_intent');
          } catch {}
          await supabase.auth.signOut();
          hasNavigatedRef.current = true;
          navigate('/signup?google_no_account=1', { replace: true });
          return;
        }

        try {
          sessionStorage.removeItem('google_oauth_intent');
        } catch {}

        let currentProfile = existingProfile;

        // 2. If no profile exists (and user came through registration), create minimal initial profile with Google user metadata
        if (!existingProfile) {
          console.log('[AuthCallback] First-time OAuth login detected. Creating initial minimal safe profile...');
          const defaultFullName = meta.full_name || meta.display_name || meta.name || '';
          const defaultStageName = meta.stage_name || defaultFullName || currentUser.email?.split('@')[0] || 'Artist';
          const avatarUrl = meta.avatar_url || meta.picture || (Array.isArray(meta.images) && meta.images[0]?.url) || null;

          // Strictly use real metadata, onboarding_completed: false, no fake roles
          currentProfile = await createProfile(currentUser, {
            fullName: defaultFullName,
            stageName: defaultStageName,
            avatar_url: avatarUrl,
            phone: null,
            city: null,
            primaryRole: meta.primary_role || null,
            additionalRoles: meta.additional_roles || [],
            onboarding_completed: false,
          });

          // Fallback refetch if createProfile did not return the profile row
          if (!currentProfile || !currentProfile.id) {
            const { data: refetched } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', currentUser.id)
              .maybeSingle();
            currentProfile = refetched;
          }
        }

        if (refreshUserData) {
          await refreshUserData();
        }

        // Fetch user creator roles for profile completion check
        const { data: userRolesData } = await supabase
          .from('creator_roles')
          .select('role')
          .eq('user_id', currentUser.id);
        const userRoles = (userRolesData || []).map((r) => r.role);

        // 3. Remove OAuth query/hash data from address bar without page reload
        if (typeof window !== 'undefined' && window.history?.replaceState) {
          window.history.replaceState(null, '', window.location.pathname);
        }

        // 4. Retrieve saved redirect destination if present, otherwise default to '/'
        let targetDestination = '/';
        try {
          const savedRedirect = sessionStorage.getItem('auth_redirect_destination');
          if (savedRedirect && savedRedirect !== '/dashboard' && savedRedirect !== '/login' && savedRedirect !== '/auth/callback') {
            targetDestination = savedRedirect;
          }
        } catch {
          // Ignore storage access exceptions
        }

        // 5. Route based on full completion requirements
        hasNavigatedRef.current = true;
        if (isProfileComplete(currentProfile, userRoles)) {
          try {
            sessionStorage.removeItem('auth_redirect_destination');
          } catch {}
          console.log('[AuthCallback] Profile complete. Navigating to:', targetDestination);
          navigate(targetDestination, { replace: true });
        } else {
          console.log('[AuthCallback] Profile incomplete. Navigating to /edit-profile?firstTime=true');
          if (targetDestination && targetDestination !== '/' && targetDestination !== '/dashboard') {
            try {
              sessionStorage.setItem('auth_redirect_destination', targetDestination);
            } catch {}
          }
          const redirectQuery = targetDestination && targetDestination !== '/' && targetDestination !== '/dashboard'
            ? `&redirect=${encodeURIComponent(targetDestination)}`
            : '';
          navigate(`/edit-profile?firstTime=true${redirectQuery}`, {
            replace: true,
            state: { from: targetDestination },
          });
        }
      } catch (err) {
        console.error('[AuthCallback] Session processing exception:', err);
        processingRef.current = false; // Allow retry if first attempt failed
        if (isMounted) {
          setError(err.message || 'Error initializing your profile. Please try signing in again.');
        }
      }
    };

    const handleCallback = async () => {
      if (!isSupabaseConfigured()) {
        if (isMounted) setError('Supabase is not configured. Please set your project credentials in frontend/.env.');
        return;
      }

      try {
        console.log('[AuthCallback] Callback route mounted. Checking parameters...');
        const searchParams = new URLSearchParams(window.location.search);
        const code = searchParams.get('code');
        const queryError = searchParams.get('error_description') || searchParams.get('error');

        let hashError = null;
        if (window.location.hash) {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          hashError = hashParams.get('error_description') || hashParams.get('error');
        }

        const errorDescription = queryError || hashError;
        if (errorDescription) {
          console.error('[AuthCallback] OAuth provider returned error:', errorDescription);
          if (errorDescription.includes('Unable to exchange external code')) {
            throw new Error(
              'Google OAuth exchange failed on Supabase. This occurs when the Google Client Secret in Supabase Dashboard does not match your Google Cloud OAuth Client credentials or the Authorized Redirect URI is mismatched.'
            );
          }
          throw new Error(errorDescription);
        }

        // 1. Check if active session already exists in client storage
        const { data: initialSessionData } = await supabase.auth.getSession();
        if (initialSessionData?.session?.user) {
          console.log('[AuthCallback] Session already active on mount.');
          await processUserSession(initialSessionData.session.user);
          return;
        }

        // 2. If PKCE code is present in query parameters, exchange it
        if (code) {
          console.log('[AuthCallback] Exchanging PKCE authorization code for session...');
          try {
            const { data: exchangeData, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
            if (exchangeData?.session?.user) {
              console.log('[AuthCallback] PKCE exchange succeeded.');
              await processUserSession(exchangeData.session.user);
              return;
            }
            if (exchangeError) {
              console.warn('[AuthCallback] Direct PKCE exchange notice:', exchangeError.message);
            }
          } catch (exErr) {
            console.warn('[AuthCallback] exchangeCodeForSession exception handled:', exErr.message);
          }
        }

        // 3. For hash-based flow (#access_token=...) or detectSessionInUrl resolution
        const { data: { session: checkSession } } = await supabase.auth.getSession();
        if (checkSession?.user) {
          console.log('[AuthCallback] Session resolved via getSession.');
          await processUserSession(checkSession.user);
          return;
        }

        // 4. Listen for auth event safely (defer processing to avoid blocking callback loop)
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          (event, authSession) => {
            console.log('[AuthCallback] Auth state change event:', event);
            if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') && authSession?.user) {
              setTimeout(() => {
                processUserSession(authSession.user);
              }, 0);
            }
          }
        );
        authSubscription = subscription;

        // 5. Fallback timer check to resolve session if events were delayed
        fallbackTimer = setTimeout(async () => {
          if (!isMounted || hasNavigatedRef.current || processingRef.current) return;
          const { data: { session: retrySession } } = await supabase.auth.getSession();
          if (retrySession?.user) {
            console.log('[AuthCallback] Session resolved on fallback check.');
            await processUserSession(retrySession.user);
          } else {
            console.warn('[AuthCallback] Session could not be established after timeout.');
            setError('Authentication session could not be established. Please try signing in again.');
          }
        }, 2500);

      } catch (err) {
        console.error('[AuthCallback] Callback exception:', err.message);
        if (isMounted) {
          setError(err.message || 'Authentication could not be completed. Please try again.');
        }
      }
    };

    handleCallback();

    return () => {
      isMounted = false;
      authSubscription?.unsubscribe();
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, [navigate, createProfile, refreshUserData]);

  if (error) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
          <div className="auth-global-error" style={{ marginBottom: '1.5rem', textAlign: 'left', lineHeight: '1.5', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
          <button className="auth-btn" onClick={() => navigate('/login', { replace: true })}>
            Return to Login
          </button>
          <button
            type="button"
            className="auth-btn"
            style={{ marginTop: '0.75rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: '#fff' }}
            onClick={() => navigate('/signup', { replace: true })}
          >
            Create Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-skeleton">
      <div className="auth-skeleton-card" style={{ textAlign: 'center' }}>
        <div className="auth-skeleton-line medium" style={{ margin: '0 auto 1rem' }} />
        <div className="auth-skeleton-line short" style={{ margin: '0 auto 1rem' }} />
        <div className="auth-skeleton-line tall" style={{ margin: '0 auto' }} />
        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', marginTop: '1.5rem' }}>
          Securing your session…
        </p>
      </div>
    </div>
  );
}
